import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeroHomeConstants } from "./HeroHomeConstants";
import PrimaryButton from "../../../components/common/PrimaryButton";
import { Images } from "../../../utils/imageConstants";
import fourGif from "../../../assets/gif/four.gif";
import "./HeroHome.scss";

const HeroHome: React.FC = () => {
    const navigate = useNavigate();
    const { hero } = HeroHomeConstants;
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const heroRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isVisible, setIsVisible] = useState(true);

    const baseUrl = (import.meta.env.BASE_URL || "/").replace(/\/$/, "");
    const normalize = (s: { src: string; duration?: number } | string) => {
        const item = typeof s === "string" ? { src: s, duration: 10 } : s;
        const src = item.src.startsWith("http") ? item.src : `${baseUrl}${item.src.startsWith("/") ? "" : "/"}${item.src}`;
        return { src, duration: item.duration ?? 10 };
    };
    const slides = (hero.backgroundVideos ?? []).map(normalize);
    const videos = slides.map((s) => s.src);
    const hasVideos = slides.length > 0;
    const fallbackSrc = hero.fallbackImage || fourGif;

    const handleVideoEnded = useCallback(() => {
        setActiveIndex((i) => (i + 1) % slides.length);
    }, [slides.length]);

    const isImage = (url: string) => /\.(gif|webp|png|jpg|jpeg)(\?|$)/i.test(url);

    useEffect(() => {
        const el = heroRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([entry]) => setIsVisible(entry.isIntersecting),
            { threshold: 0.25 }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    useEffect(() => {
        videoRefs.current.forEach((v) => v?.pause());
        if (isVisible) {
            const src = videos[activeIndex];
            if (src && !isImage(src)) {
                const video = videoRefs.current[activeIndex];
                if (video) {
                    video.currentTime = 0;
                    video.play();
                }
            }
        }
    }, [activeIndex, isVisible, videos]);

    useEffect(() => {
        if (!hasVideos || slides.length <= 1) return;
        const { src, duration } = slides[activeIndex];
        if (!isImage(src)) return;
        const ms = duration * 1000;
        const t = setTimeout(() => {
            setActiveIndex((i) => (i + 1) % slides.length);
        }, ms);
        return () => clearTimeout(t);
    }, [activeIndex, hasVideos, slides]);

    const handleButtonClick = (route: string) => {
        navigate(route);
    };

    const renderBackground = () => {
        if (!hasVideos) {
            return (
                <img
                    className="heroHome-hero-background"
                    src={fallbackSrc}
                    alt="Cricket match background"
                />
            );
        }

        return (
            <div className="heroHome-hero-videos">
                <div className="heroHome-hero-videos-track">
                    {videos.map((src, index) =>
                        isImage(src) ? (
                            <img
                                key={src}
                                className={`heroHome-hero-video heroHome-hero-media${index === activeIndex ? " heroHome-hero-video--active" : ""}`}
                                src={src}
                                alt=""
                                aria-hidden={index !== activeIndex}
                            />
                        ) : (
                            <video
                                key={src}
                                ref={(el) => { videoRefs.current[index] = el; }}
                                className={`heroHome-hero-video heroHome-hero-media${index === activeIndex ? " heroHome-hero-video--active" : ""}`}
                                src={src}
                                muted
                                autoPlay={false}
                                playsInline
                                loop={slides.length === 1}
                                onEnded={slides.length > 1 ? handleVideoEnded : undefined}
                                preload="auto"
                                aria-hidden={index !== activeIndex}
                            />
                        )
                    )}
                </div>
            </div>
        );
    };

    return (
        <div ref={heroRef} className="heroHome-hero">
            {renderBackground()}
            <div className="heroHome-hero-overlay" />
            <div className="heroHome-hero-content">
                <div className="heroHome-mobile-topbar">
                    <button className="heroHome-mobile-icon-btn" type="button" aria-label="Open menu">
                        <span className="heroHome-hamburger" aria-hidden="true">
                            <span></span>
                            <span></span>
                            <span></span>
                        </span>
                    </button>
                    <div className="heroHome-mobile-brand">
                        <img src={Images.main_logo} alt="CricCenter logo" className="heroHome-mobile-logo" />
                        <span className="heroHome-mobile-brand-text">CricCenter</span>
                    </div>
                    <div className="heroHome-mobile-actions">
                        <button className="heroHome-mobile-icon-btn" type="button" aria-label="Notifications">
                            <img src={Images.notification_icon} alt="" className="heroHome-mobile-notification" />
                        </button>
                        <button className="heroHome-mobile-avatar-btn" type="button" aria-label="Profile">
                            <img src={Images.dummy_profile} alt="" className="heroHome-mobile-avatar" />
                        </button>
                    </div>
                </div>
                <div className="heroHome-hero-text">
                    <div className="heroHome-hero-title-container">
                        <h1 className="heroHome-hero-title">
                            {hero.title.split('\n').map((line, index) => (
                                <React.Fragment key={index}>
                                    {line}
                                    {index < hero.title.split('\n').length - 1 && <br />}
                                </React.Fragment>
                            ))}
                        </h1>
                        {/* Example: Use PrimaryButton in a second location */}
                        {/* {hero.additionalButtons && hero.additionalButtons
                            .filter(btn => btn.variant === 'primary')
                            .map((button, index) => (
                                <PrimaryButton
                                    key={index}
                                    text={button.text}
                                    onClick={() => handleButtonClick(button.route)}
                                />
                            ))
                        } */}
                    </div>
                    <div className="heroHome-hero-description-container">
                        <p className="heroHome-hero-description">
                            {hero.description}
                        </p>
                        <div className="heroHome-hero-buttons">
                            {hero.buttons.map((button, index) => {
                                if (button.variant === 'primary') {
                                    return (
                                        <PrimaryButton
                                            key={index}
                                            text={button.text}
                                            onClick={() => handleButtonClick(button.route)}
                                        />
                                    );
                                }
                                return (
                                    <button
                                        key={index}
                                        className={`btn-${button.variant}`}
                                        onClick={() => handleButtonClick(button.route)}
                                    >
                                        {button.text}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HeroHome;
